import { useEffect, useState } from "react";
import { Loader2, MessageCircleHeart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/components/Bits";

export const BetaFeedbackAdmin = () => {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState("");
  const [sums, setSums] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/beta-summaries").then((r) => setSums(r.data)).catch(() => {}); }, []);
  const summarise = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/beta-summaries/generate"); setSums([r.data, ...sums.filter((s) => s.week !== r.data.week)]); toast.success("Summary ready"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const load = () => api.get("/admin/beta-feedback").then((r) => { setD(r.data); setText(r.data.questions.join("\n")); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const save = async () => {
    try { await api.put("/admin/beta-questions", { questions: text.split("\n") }); toast.success("Questions saved"); setEdit(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const byQ = d.questions.map((q) => [q, d.answers.filter((a) => a.question === q)]);
  const other = d.answers.filter((a) => !d.questions.includes(a.question));
  return (
    <section className="mb-12 rounded-2xl border border-border bg-card p-6" data-testid="beta-feedback-admin">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="eyebrow flex items-center gap-2"><MessageCircleHeart className="h-3.5 w-3.5" />Weekly beta question · {d.current_week}</div>
          <p className="font-serif text-xl text-navy mt-1" data-testid="beta-current-question">This week: {d.current_question}</p>
          <p className="text-xs text-muted-foreground mt-1">Beta testers see one question a week on their dashboard. The list rotates automatically while beta mode is on.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit(!edit)} data-testid="beta-questions-edit">{edit ? "Close" : "Edit questions"}</Button>
      </div>
      <div className="mt-6 rounded-xl border-2 border-sun bg-sun/10 p-5" data-testid="beta-summaries">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className="font-medium text-navy">What to fix first</p><p className="text-xs text-muted-foreground">Emailed to you every Monday at 8am for the week before, and saved here.</p></div>
          <Button size="sm" onClick={summarise} disabled={busy} className="rounded-full bg-navy text-white" data-testid="beta-summarise-btn">{busy && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Summarise this week</Button>
        </div>
        {sums.length === 0 ? <p className="mt-3 text-sm text-muted-foreground" data-testid="beta-summaries-empty">No summaries yet. The first one arrives the Monday after testers start answering.</p>
          : sums.map((s, i) => (
            <details key={s.week} open={i === 0} className="mt-3 rounded-lg bg-white p-4" data-testid={`beta-summary-${s.week}`}>
              <summary className="cursor-pointer text-sm font-medium text-navy">{s.week} · {s.answers} answers</summary>
              <div className="mt-3 text-sm whitespace-pre-wrap leading-relaxed">{s.text.replace(/\*\*/g, "").replace(/^#{1,3} /gm, "").replace(/^\*(.+)\*$/gm, "$1")}</div>
            </details>
          ))}
      </div>
      {edit && (
        <div className="mt-4">
          <Textarea rows={9} value={text} onChange={(e) => setText(e.target.value)} data-testid="beta-questions-text" className="bg-white text-sm" />
          <p className="text-xs text-muted-foreground mt-1">One question per line.</p>
          <Button size="sm" onClick={save} className="mt-2 rounded-full bg-navy text-white" data-testid="beta-questions-save">Save questions</Button>
        </div>
      )}
      <div className="mt-6 space-y-3">
        {[...byQ, ...(other.length ? [["Earlier questions", other]] : [])].map(([q, rows], i) => (
          <details key={q} className="rounded-xl bg-secondary p-4" open={i === d.current_index} data-testid={`beta-q-group-${i}`}>
            <summary className="cursor-pointer text-sm font-medium text-navy">{q} <span className="font-mono text-xs text-muted-foreground">· {rows.length} answer{rows.length === 1 ? "" : "s"}</span></summary>
            <ul className="mt-3 space-y-2">{rows.map((a) => (
              <li key={`${a.name}-${a.week}-${a.created_at}`} className="rounded-lg bg-white p-3 text-sm">
                <p>{a.answer}</p>
                <p className="mt-1 text-xs text-muted-foreground">{a.name} · {a.business_name || "—"} · {a.week} · {fmtDate(a.updated_at || a.created_at)}</p>
              </li>
            ))}{rows.length === 0 && <li className="text-xs text-muted-foreground">No answers yet.</li>}</ul>
          </details>
        ))}
      </div>
    </section>
  );
};
